import { useCallback, useEffect, useRef, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import HvnlyNabAlert from './Alert/HvnlyNabAlert';
import { useTheme } from './hooks/useTheme';
import LoadingOverlay from './LoadingOverlay/LoadingOverlay';
import Content from './settings/components/Content/Content';
import Footer from './settings/components/Footer/Footer';
import Header from './settings/components/Header/Header';
import Preloader from './settings/components/Preloader/Preloader';
import Tabs from './settings/components/Tabs/Tabs';
import { TABS } from './settings/components/utils/constants';
import { useSettingsAPI } from './settings/hooks/useSettingsAPI';
import './SettingsApp.css';
import Toast from './Toast/Toast';

function App() {
  const [toasts, setToasts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadingStage, setLoadingStage] = useState(__("Initializing Settings...", "havenlytics"));
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTab = localStorage.getItem('hvnly-active-tab');
      return savedTab ? JSON.parse(savedTab) : 'design';
    }
    return 'design';
  });
  
  const { isDarkMode, toggleTheme } = useTheme();
  
  // Track toast IDs to prevent duplicates
  const lastToastRef = useRef({ id: null, time: 0 });
  
  // Track settings per tab for saving
  const [tabSettings, setTabSettings] = useState({});
  
  // Track if data has been loaded from API
  const [apiDataLoaded, setApiDataLoaded] = useState(false);

  // Define showToast FIRST
  const showToast = useCallback((type, title, message, animation = 'bounceIn') => {
    const toastKey = `${type}-${title}-${message}-${animation}`;
    const now = Date.now();
    
    if (lastToastRef.current.id === toastKey && now - lastToastRef.current.time < 2000) {
      return;
    }
    
    lastToastRef.current = {
      id: toastKey,
      time: now
    };
    
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message, animation }]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  }, []);

  // Now useSettingsAPI can use showToast
  const { 
    settings, 
    isLoading: apiLoading, 
    loadAllSettings, 
    saveSettingsGroup, 
    resetSettingsGroup, 
    saveAllSettings, 
    resetAllSettings 
  } = useSettingsAPI(showToast);

  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      // console.log('🔵 [App] Settings loaded from API:', settings);
      // console.log('🔵 [App] General settings:', settings.general);
      // console.log('🔵 [App] Design settings:', settings.design);
      // console.log('🔵 [App] Properties settings:', settings.properties);
      // console.log('🔵 [App] Shortcodes settings:', settings.shortcodes);
      setTabSettings(settings);
      setApiDataLoaded(true);
    }
  }, [settings]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hvnly-active-tab', JSON.stringify(activeTab));
    }
  }, [activeTab]);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      // console.log('🟡 [App] Loading settings from API...');
      const loadedSettings = await loadAllSettings();
      if (loadedSettings) {
        // console.log('✅ [App] Settings loaded successfully');
      } else {
        // console.error('❌ [App] Failed to load settings');
      }
      setTimeout(() => {
        setLoadingProgress(100);
        setTimeout(() => {
          setIsInitialLoading(false);
        }, 300);
      }, 500);
    };
    loadSettings();
  }, []);

  // Simulate loading stages for settings
  useEffect(() => {
    if (isInitialLoading) {
      const stages = [
        { stage: __("Initializing Settings Module...", "havenlytics"), progress: 25 },
        { stage: __("Loading General Settings...", "havenlytics"), progress: 55 },
        { stage: __("Finalizing Setup...", "havenlytics"), progress: 95 }
      ];

      let currentStage = 0;

      const progressInterval = setInterval(() => {
        if (currentStage < stages.length) {
          setLoadingStage(stages[currentStage].stage);
          setLoadingProgress(stages[currentStage].progress);
          currentStage++;
        } else {
          clearInterval(progressInterval);
        }
      }, 600);

      return () => clearInterval(progressInterval);
    }
  }, [isInitialLoading]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Handle tab settings change - CRITICAL FIX
  const handleTabSettingsChange = (tabId, settingsData) => {
    // console.log(`🟠 [App] handleTabSettingsChange - Tab: ${tabId}, Data:`, settingsData);
    
    // Map dropdown tabs to their parent groups
    const parentGroupMap = {
      'currency': 'general',
      'misc-settings': 'general',
      'search-property': 'search',
      'search-result': 'search'
    };
    
    // If it's a dropdown tab, update the parent group
    if (parentGroupMap[tabId]) {
      const parentGroup = parentGroupMap[tabId];
      // console.log(`🟠 [App] ${tabId} is a dropdown tab, updating parent group: ${parentGroup}`);
      
      setTabSettings(prev => {
        const currentParentData = prev[parentGroup] || {};
        const mergedData = { ...currentParentData, ...settingsData };
        // console.log(`🟠 [App] Merged data for ${parentGroup}:`, mergedData);
        return {
          ...prev,
          [parentGroup]: mergedData
        };
      });
    } else {
      // For regular tabs (design, properties, shortcodes, search, general, etc.), update directly
      // console.log(`🟠 [App] ${tabId} is a regular tab, updating directly`);
      setTabSettings(prev => ({
        ...prev,
        [tabId]: settingsData
      }));
    }
  };

  // Save only the current tab using the data passed from component
  const handleSaveTab = async (tabName, data) => {
    // console.log(`💾 [App] handleSaveTab called - Tab: ${tabName}, Data:`, data);
    setIsLoading(true);
    
    const settingsToSave = data;
    
    if (!settingsToSave || Object.keys(settingsToSave).length === 0) {
      // console.warn('⚠️ [App] No settings to save');
      showToast('error', __('Error', 'havenlytics'), __('No settings to save', 'havenlytics'));
      setIsLoading(false);
      return false;
    }
    
    // console.log(`📤 [App] Calling saveSettingsGroup for group: ${tabName}`);
    const result = await saveSettingsGroup(tabName, settingsToSave);
    // console.log(`📤 [App] Save result for ${tabName}:`, result);
    
    if (result && result.success) {
      const tabDisplayName = tabName.charAt(0).toUpperCase() + tabName.slice(1);
      showToast('success', __('Success', 'havenlytics'), `${tabDisplayName} ${__('settings saved successfully!', 'havenlytics')}`);
      setIsLoading(false);
      return true;
    } else {
      if (!result?.message) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to save settings', 'havenlytics'));
      }
      setIsLoading(false);
      return false;
    }
  };

  // Reset only the current tab
  const handleResetTab = async (tabName) => {
    // console.log(`🔄 [App] handleResetTab called - Tab: ${tabName}`);
    
    setIsLoading(true);
    const result = await resetSettingsGroup(tabName);
    // console.log(`📤 [App] Reset result for ${tabName}:`, result);
    
    if (result && result.success) {
      const tabDisplayName = tabName.charAt(0).toUpperCase() + tabName.slice(1);
      showToast('success', __('Success', 'havenlytics'), `${tabDisplayName} ${__('settings reset to defaults!', 'havenlytics')}`);
      await loadAllSettings();
      setIsLoading(false);
      return true;
    } else {
      if (!result?.message) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to reset settings', 'havenlytics'));
      }
      setIsLoading(false);
      return false;
    }
  };

  // Save ALL tabs (used by header Save All button)
  const handleSaveAll = async () => {
    // console.log('💾 [App] Save All clicked - Current tabSettings:', tabSettings);
    // console.log('📦 [App] Design settings before save:', tabSettings.design);
    // console.log('📦 [App] Properties settings before save:', tabSettings.properties);
    // console.log('📦 [App] Shortcodes settings before save:', tabSettings.shortcodes);
    // console.log('📦 [App] General settings before save:', tabSettings.general);
    
    setIsLoading(true);
    const result = await saveAllSettings(tabSettings);
    // console.log('📤 [App] Save All result:', result);
    
    if (result && result.success) {
      // console.log('✅ [App] Save All successful');
      showToast('success', __('Success', 'havenlytics'), __('All settings saved successfully!', 'havenlytics'));
    } else if (result && !result.success) {
      // console.log('❌ [App] Save All failed');
      if (!result.message) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to save all settings', 'havenlytics'));
      }
    }
    
    setIsLoading(false);
  };

  // Reset ALL tabs (used by header Reset All button)
  const handleResetAll = async () => {
    // console.log('🔄 [App] Reset All clicked');
    
    if (window.HvnlyNabAlert) {
      const result = await window.HvnlyNabAlert.confirm(
        __('Are you sure you want to reset ALL settings? This cannot be undone.', 'havenlytics'),
        __('Reset All Settings?', 'havenlytics')
      );
      
      if (result !== 'confirm') {
        // console.log('❌ Reset All cancelled by user');
        return;
      }
    } else {
      const confirmed = window.confirm(__('Are you sure you want to reset ALL settings? This cannot be undone.', 'havenlytics'));
      if (!confirmed) return;
    }
    
    setIsLoading(true);
    const resetResult = await resetAllSettings();
    // console.log('📤 [App] Reset All result:', resetResult);
    
    if (resetResult && resetResult.success) {
      showToast('success', __('Success', 'havenlytics'), __('All settings reset to defaults!', 'havenlytics'));
      await loadAllSettings();
    } else if (resetResult && !resetResult.success) {
      if (!resetResult.message) {
        showToast('error', __('Error', 'havenlytics'), __('Failed to reset all settings', 'havenlytics'));
      }
    }
    
    setIsLoading(false);
  };

  const handleTabChange = (tabId) => {
    // console.log(`📑 [App] Tab changed to: ${tabId}`);
    setActiveTab(tabId);
  };

  // Show preloader while initial loading is true
  if (isInitialLoading) {
    return <Preloader stage={loadingStage} progress={loadingProgress} showProgress={true} />;
  }

  // Get current tab settings - handle dropdown tabs
  const getCurrentTabSettings = () => {
    const parentGroupMap = {
      'currency': 'general',
      'misc-settings': 'general',
      'search-property': 'search',
      'search-result': 'search'
    };
    
    if (parentGroupMap[activeTab]) {
      const parentGroup = parentGroupMap[activeTab];
      // console.log(`🟢 [App] ${activeTab} is a dropdown tab, getting settings from parent group: ${parentGroup}`);
      return tabSettings[parentGroup] || {};
    }
    
    // console.log(`🟢 [App] Regular tab ${activeTab}, getting direct settings`);
    return tabSettings[activeTab] || {};
  };

  const currentTabSettings = getCurrentTabSettings();
  // console.log('🟢 [App] Rendering main app');
  // console.log('🟢 [App] Active Tab:', activeTab);
  // console.log('🟢 [App] Current tab settings for', activeTab, ':', currentTabSettings);
  // console.log('🟢 [App] Full tabSettings:', tabSettings);

  return (
    <div className={`hvnly--settings--app ${isDarkMode ? 'dark-mode' : ''}`}>
      <HvnlyNabAlert />
      
      <div className="hvnly-settings-api__container">
        <Header 
          onSaveAll={handleSaveAll}
          onResetAll={handleResetAll}
        />
        
        <div className="hvnly-settings-api__main">
          <Tabs 
            tabs={TABS} 
            activeTab={activeTab}
            onTabChange={handleTabChange} 
          />
          
          <Content 
            activeTab={activeTab}
            onSaveTab={handleSaveTab}
            onResetTab={handleResetTab}
            settingsData={currentTabSettings}
            onSettingsChange={handleTabSettingsChange}
            showToast={showToast}
            key={activeTab}
          />
        </div>
        
        <Footer />
      </div>
      
      <button 
        id="themeToggle" 
        className="dark-mode-toggle"
        onClick={toggleTheme}
        aria-label={__('Toggle dark mode', 'havenlytics')}
      >
        <i className={isDarkMode ? 'fas fa-sun' : 'fas fa-moon'}></i>
      </button>
      
      <LoadingOverlay isLoading={isLoading || apiLoading} />

      <div 
        id="hvnlyToastContainer" 
        className="hvnly-toast-container"
        style={{
          position: 'fixed',
          top: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pointerEvents: 'none'
        }}
      >
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            type={toast.type}
            title={toast.title}
            message={toast.message}
            onClose={() => removeToast(toast.id)}
            autoClose={true}
            autoCloseDuration={3000}
            position="top-center"
          />
        ))}
      </div>
    </div>
  );
}

export default App;